/* eslint-disable */
import toastStore from '@/store/toast.store';
import appStore from '@/store/app.store';
import { appEvents } from '@/store/app.events';
import i18n from '@/i18n';

import syncService from '@/service/sync.service';

import { isNativePlatform, resolveAppUrl, openAuthSession } from '@/utils/capacitor';

// --- Monitor de saúde do servidor (Bad gateway / servidor fora do ar) ---
// navigator.onLine só diz se há REDE (Wi-Fi ligado); um 502 com rede ok não é detectado.
// Contamos falhas consecutivas da NOSSA api (erro de rede ou 502/503/504) e, ao atingir
// o limiar, tratamos como offline (fila de sync, caches, banner) até a recuperação.
let serverFailureCount = 0;
let serverDown = false;
let lastServerDownToast = 0;
const SERVER_DOWN_THRESHOLD = 2;
const SERVER_DOWN_TOAST_MS = 10000;
const isEffectivelyOffline = () => !navigator.onLine || serverDown;

const getApiPath = (resource) => {
    try {
        let url = null;
        const origin = isNativePlatform() ? new URL(resolveAppUrl('')).origin : window.location.origin;
        if (typeof resource === 'string') {
            if (resource.startsWith('http://') || resource.startsWith('https://') || resource.startsWith('//')) {
                const target = new URL(resource, origin);
                if (target.origin !== origin && target.origin !== window.location.origin) return '';
                url = target;
            } else {
                url = new URL(resource, origin);
            }
        } else if (resource instanceof Request) {
            const target = new URL(resource.url, origin);
            if (target.origin !== origin && target.origin !== window.location.origin) return '';
            url = target;
        }
        const path = url ? url.pathname : '';
        return path.startsWith('/api/') ? path : '';
    } catch (e) {
        return '';
    }
};

const noteApiSuccess = () => {
    if (serverFailureCount === 0 && !serverDown) return;
    serverFailureCount = 0;
    if (serverDown) {
        serverDown = false;
        if (navigator.onLine) {
            try {
                appStore.commit('SET_ONLINE_STATUS', true);
                const msg = i18n.global.t('toast.serverRecovered') || 'Server back online. Syncing changes...';
                toastStore.dispatch('addMessage', { message: msg, icon: 'success', ttl: 3000 });
            } catch (e) {}
            // Mesma ordem do App.vue: replay primeiro, reload depois (via app:online).
            Promise.race([
                Promise.resolve(syncService.drain()),
                new Promise((resolve) => setTimeout(resolve, 20000))
            ]).catch(() => {}).finally(() => {
                try { appEvents.emit('app:online'); } catch (e) {}
            });
        }
    }
};

const noteApiFailure = () => {
    serverFailureCount += 1;
    if (serverDown || serverFailureCount < SERVER_DOWN_THRESHOLD) return;
    serverDown = true;
    try {
        appStore.commit('SET_ONLINE_STATUS', false);
        const now = Date.now();
        if (now - lastServerDownToast > SERVER_DOWN_TOAST_MS) {
            lastServerDownToast = now;
            const msg = i18n.global.t('toast.serverUnreachable') || 'Server unreachable. Offline mode: your saved content is available.';
            toastStore.dispatch('addMessage', { message: msg, icon: 'warning', ttl: 5000 });
        }
    } catch (e) {}
};

let myClientId = sessionStorage.getItem('my_client_id');
if (!myClientId) {
    myClientId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15));
    sessionStorage.setItem('my_client_id', myClientId);
}
window.myClientId = myClientId;

const isQueueableRequest = (resource, method) => {
    try {
        let urlPath = '';
        if (typeof resource === 'string') {
            urlPath = new URL(resource, window.location.origin).pathname;
        } else if (resource instanceof Request) {
            urlPath = new URL(resource.url, window.location.origin).pathname;
        }

        // Notes: POST /api/playlist/:id/song/:songId/notes
        if (urlPath.match(/^\/api\/playlist\/[^/]+\/song\/[^/]+\/notes$/) && method === 'POST') {
            return true;
        }
        // Drawing: POST / DELETE /api/playlist/:id/song/:songId/drawing
        if (urlPath.match(/^\/api\/playlist\/[^/]+\/song\/[^/]+\/drawing$/) && ['POST', 'DELETE'].includes(method)) {
            return true;
        }
        // Notation: POST /api/playlist/:id/song/:songId/notation
        if (urlPath.match(/^\/api\/playlist\/[^/]+\/song\/[^/]+\/notation$/) && method === 'POST') {
            return true;
        }
        // Song transposition/lyrics update in playlist: POST /api/playlist/:id/song/:songId
        if (urlPath.match(/^\/api\/playlist\/[^/]+\/song\/[^/]+$/) && method === 'POST') {
            return true;
        }
        // Playlist favorite toggle: PUT /api/playlist/:id/favorite
        if (urlPath.match(/^\/api\/playlist\/[^/]+\/favorite$/) && method === 'PUT') {
            return true;
        }
        // Playlist song transpose: GET /api/playlist/:id/song/transpose/:songId
        if (urlPath.match(/^\/api\/playlist\/[^/]+\/song\/transpose\/[^/]+$/)) {
            return true;
        }
        // User favorite songs: POST / DELETE /api/user/favorite-songs
        if (urlPath.match(/^\/api\/user\/favorite-songs/) && ['POST', 'DELETE'].includes(method)) {
            return true;
        }
    } catch (e) {
        return false;
    }
    return false;
};

const { fetch: originalFetch } = window;
window.fetch = async (...args) => {

    let [resource, config] = args;
    
    // Ensure config exists
    if (!config) {
        config = {};
    }

    // Cross-origin targets (e.g. SoundFont/jsDelivr CDN) must pass through
    // untouched: credentials:include + Authorization headers force a CORS
    // preflight that CDNs reject, breaking the request.
    let isSameOrigin = true;
    try {
        const origin = isNativePlatform() ? new URL(resolveAppUrl('')).origin : window.location.origin;
        if (typeof resource === 'string') {
            if (resource.startsWith('http://') || resource.startsWith('https://') || resource.startsWith('//')) {
                const targetUrl = new URL(resource, origin);
                isSameOrigin = targetUrl.origin === origin || targetUrl.origin === window.location.origin;
            }
        } else if (resource instanceof Request) {
            const targetUrl = new URL(resource.url, origin);
            isSameOrigin = targetUrl.origin === origin || targetUrl.origin === window.location.origin;
        }
    } catch (e) {
        isSameOrigin = true;
    }

    // When running natively inside Capacitor, ensure credentials, resolved baseUrl, and session ID
    // (same-origin API requests only — never touch external CDNs)
    if (isNativePlatform() && isSameOrigin) {
        if (!config.credentials) {
            config.credentials = 'include';
        }
        if (typeof resource === 'string') {
            if (resource.startsWith('/') || !resource.startsWith('http')) {
                resource = resolveAppUrl(resource);
            }
        } else if (resource instanceof Request) {
            const url = resource.url;
            if (url.startsWith('/') || !url.startsWith('http')) {
                resource = new Request(resolveAppUrl(url), resource);
            }
        }
        const sessionId = localStorage.getItem('triad_session_id');
        if (sessionId) {
            if (!config.headers) {
                config.headers = {};
            }
            if (config.headers instanceof Headers) {
                config.headers.set('X-Session-ID', sessionId);
                config.headers.set('Authorization', `Bearer ${sessionId}`);
            } else if (Array.isArray(config.headers)) {
                config.headers.push(['X-Session-ID', sessionId]);
                config.headers.push(['Authorization', `Bearer ${sessionId}`]);
            } else {
                config.headers['X-Session-ID'] = sessionId;
                config.headers['Authorization'] = `Bearer ${sessionId}`;
            }
            if (resource instanceof Request) {
                try {
                    resource.headers.set('X-Session-ID', sessionId);
                    resource.headers.set('Authorization', `Bearer ${sessionId}`);
                } catch (e) {}
            }
        }
    }
    
    // Only inject X-Client-ID for same-origin or relative API requests to avoid CORS preflight failures on external CDNs
    // (isSameOrigin computed above)

    if (isSameOrigin) {
        // Ensure config.headers exists and inject X-Client-ID
        if (!config.headers) {
            config.headers = {};
        }
        if (config.headers instanceof Headers) {
            config.headers.set('X-Client-ID', myClientId);
        } else if (Array.isArray(config.headers)) {
            config.headers.push(['X-Client-ID', myClientId]);
        } else {
            config.headers['X-Client-ID'] = myClientId;
        }
    }

    const method = (config.method || 'GET').toUpperCase();

    // Palco Local / WebRTC Signaling: Never block local stage-session signaling requests even if offline
    let urlPathname = '';
    try {
        if (typeof resource === 'string') {
            urlPathname = new URL(resource, window.location.origin).pathname;
        } else if (resource instanceof Request) {
            urlPathname = new URL(resource.url, window.location.origin).pathname;
        }
    } catch (e) {}

    if (urlPathname.startsWith('/api/stage-session/')) {
        return originalFetch(resource, config);
    }

    // Offline handling for mutating and queueable requests
    // (vale para sem-rede E para servidor fora do ar — Bad gateway com Wi-Fi ligado)
    if (isEffectivelyOffline()) {
        if (isQueueableRequest(resource, method)) {
            console.log('Queueing request while offline:', method, resource);
            const urlStr = typeof resource === 'string' ? resource : resource.url;
            syncService.enqueue({
                url: urlStr,
                method,
                body: config.body,
                headers: config.headers
            });

            try {
                const now = Date.now();
                if (!window._lastOfflineToastTime || now - window._lastOfflineToastTime > 5000) {
                    window._lastOfflineToastTime = now;
                    const msg = i18n.global.t('toast.offlineQueued') || 'Alterações salvas localmente. Serão sincronizadas automaticamente quando você estiver online.';
                    toastStore.dispatch('addMessage', {
                        message: msg,
                        icon: 'info',
                        ttl: 3000
                    });
                }
            } catch (e) {}

            return {
                ok: true,
                status: 200,
                offlineQueued: true,
                json: async () => ({ status: 'queued', offline: true }),
                text: async () => JSON.stringify({ status: 'queued', offline: true }),
                clone: function() { return this; }
            };
        }

        if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
            console.warn('Write request blocked while offline:', resource);
            try {
                const msg = i18n.global.t('toast.offlineWriteError') || 'Você está offline. Alterações requerem conexão com a internet.';
                toastStore.dispatch('addMessage', {
                    message: msg,
                    icon: 'warning',
                    ttl: 4000
                });
            } catch (e) {}
            return {
                ok: false,
                status: 503,
                json: async () => ({ error: 'OFFLINE', message: 'You are offline' }),
                text: async () => 'You are offline'
            };
        }
    }

    let response;
    try {
        response = await originalFetch(resource, config);
    } catch (fetchError) {
        // Erro de rede na nossa api = servidor/host inalcançável (conta para serverDown).
        if (getApiPath(resource)) {
            noteApiFailure();
        }
        // Gap fechado: com rede "ligada" mas inalcançável, a pré-checagem acima deixou
        // passar e a mutação se perderia aqui. Enfileira (fila dedupada pelo replay).
        if (method !== 'GET' && isQueueableRequest(resource, method)) {
            try {
                const urlStr = typeof resource === 'string' ? resource : resource.url;
                syncService.enqueue({
                    url: urlStr,
                    method,
                    body: config.body,
                    headers: config.headers
                });
            } catch (e) {}
        }
        if (isEffectivelyOffline()) {
            console.warn('Network request failed while offline:', resource);
            return {
                ok: false,
                status: 503,
                json: async () => ({ error: 'OFFLINE', message: 'Offline network error' }),
                text: async () => 'Offline network error'
            };
        }
        throw fetchError;
    }

    // Saúde do servidor: 502/503/504 da nossa api contam como queda; qualquer outra
    // resposta HTTP (mesmo 4xx) prova que o servidor respondeu e zera o contador.
    if (getApiPath(resource)) {
        if (response.status === 502 || response.status === 503 || response.status === 504) {
            noteApiFailure();
        } else {
            noteApiSuccess();
        }
    }

    // Invalidate Service Worker cache when mutating operations succeed
    if (response.ok && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            try {
                let urlPath = '';
                if (typeof resource === 'string') {
                    urlPath = new URL(resource, window.location.origin).pathname;
                } else if (resource instanceof Request) {
                    urlPath = new URL(resource.url, window.location.origin).pathname;
                }
                
                if (urlPath.startsWith('/api/playlist')) {
                    navigator.serviceWorker.controller.postMessage({
                        type: 'INVALIDATE_API_CACHE',
                        pattern: '^/api/playlist'
                    });
                } else if (urlPath.startsWith('/api/song')) {
                    navigator.serviceWorker.controller.postMessage({
                        type: 'INVALIDATE_API_CACHE',
                        pattern: '^/api/song'
                    });
                } else if (urlPath.startsWith('/api/schedule')) {
                    navigator.serviceWorker.controller.postMessage({
                        type: 'INVALIDATE_API_CACHE',
                        pattern: '^/api/schedule'
                    });
                } else if (urlPath.startsWith('/api/team')) {
                    navigator.serviceWorker.controller.postMessage({
                        type: 'INVALIDATE_API_CACHE',
                        pattern: '^/api/team'
                    });
                }
            } catch (swErr) {
                console.warn('Could not post invalidate message to Service Worker:', swErr);
            }
        }
    }

    // limit reached interceptor
    if (response.status === 403) {
        try {
            const bodyClone = await response.clone().json();
            if (bodyClone && (bodyClone.error === 'LIMIT_REACHED' || bodyClone.message === 'LIMIT_REACHED')) {
                const resourceName = bodyClone.resource;
                const limit = bodyClone.limit;
                const msg = i18n.global.t('toast.limitReached', { resource: resourceName, limit: limit });
                
                toastStore.dispatch('addMessage', {
                    message: msg,
                    icon: 'warning',
                    ttl: 5000
                });
            }
        } catch (e) {
            // Ignore parse errors if the 403 is not JSON
        }
    }

    // response interceptor (session expiration redirect ONLY when online AND server up)
    if (navigator.onLine && !serverDown && (response.status === 401 || (response.redirected && response.url && response.url.indexOf("/login") > -1))) {
        let requestUrl = '';
        try {
            requestUrl = typeof resource === 'string' ? resource : (resource?.url || '');
        } catch (e) {}

        const isAuthProbe = requestUrl.includes('/api/ably/auth') || requestUrl.includes('/login') || requestUrl.includes('/auth/callback');
        const isAuthInProgress = sessionStorage.getItem('triad_auth_in_progress') === 'true';

        // Only treat as genuine session expiry if not an auth probe and login is not actively in progress
        if (!isAuthProbe && !isAuthInProgress) {
            console.log('Session Expired for request:', requestUrl);
            if (isNativePlatform()) {
                const hadSession = !!localStorage.getItem('triad_session_id');
                if (hadSession) {
                    localStorage.removeItem('triad_session_id');
                    localStorage.removeItem('triad_offline_user');
                    appEvents.emit('app:session-expired');
                }
            } else {
                window.location.href = "/login";
            }
        }
        return {
            ok: false,
            status: 401,
            json: async () => ({ error: 'Unauthorized', message: 'Session Expired' }),
            text: async () => 'Unauthorized'
        };
    }
    const json = () =>
        response
            .clone()
            .json()

    response.json = json;
    return response;
};